import type {
    WordId,
    SentenceId,
    Timestamp,
    IntervalMs
} from '$lib/classes/data/_primitives';
import type {UserWordJson} from '$lib/classes/data/user-word-json';

export enum Status {
    Known = 'KNOWN',
    Learn = 'LEARN',
}

export class UserWord {

    constructor(
        public readonly id: WordId,
        public readonly status: Status,
        public readonly sentenceId: SentenceId | null,
        public readonly added: Timestamp,
        public readonly toLearnNext: Timestamp,
        public readonly interval: IntervalMs = 54000000,
    ) {}

    static fromJson(json: UserWordJson): UserWord {
        return new UserWord(
            json.id,
            json.status,
            json.sentenceId,
            json.added,
            json.toLearnNext,
            json.interval,
        );
    }

    toJson(): UserWordJson {
        return {
            id: this.id,
            status: this.status,
            sentenceId: this.sentenceId,
            added: this.added,
            toLearnNext: this.toLearnNext,
            interval: this.interval,
        }
    }

    private copy(changes: Partial<Omit<UserWord, 'added'>>): UserWord {
        return new UserWord(
            changes.id ?? this.id,
            changes.status ?? this.status,
            changes.sentenceId === undefined ? this.sentenceId : changes.sentenceId,
            this.added,
            changes.toLearnNext ?? this.toLearnNext,
            changes.interval ?? this.interval
        );
    }

    withId(newId: WordId): UserWord {
        return this.copy({id: newId});
    }

    withStatus(status: Status): UserWord {
        return this.copy({status});
    }

    withSentenceId(sentenceId: SentenceId | null): UserWord {
        return this.copy({sentenceId});
    }

    withToLearnNextAndInterval(toLearnNext: Timestamp, interval: IntervalMs): UserWord {
        return this.copy({toLearnNext, interval});
    }
}